import { AuditedRowSpec } from '../../../AuditedFinancialsVisualisation/auditedStatementLayout';

/**
 * Row model for the Provisional Financials statement view (RECUR-27786). Provisional renders the
 * audited redesign's (RECUR-27010) statutory layouts as-is, so both statements read identically.
 * Rows join by `classificationName` (`src`), never `classificationId`, as in `AuditedStatementTable`.
 *
 * `Balance with Statutory Authorities` (10) and `CAC` (5) have no rows here: the extractor files those
 * amounts under `Other Advances / Deposits` (16) and `Other Expenses` (60).
 *
 * Both join directions matter, and only one is harmless:
 *   - a layout `src` the payload lacks   → renders '—'. Harmless.
 *   - a payload row absent from the layout → would render NOWHERE. Silent omission of financial
 *     data on an underwriting surface, which is not acceptable.
 * `appendUnmatchedRows` below closes the second case for every fixed layout, so any class 10 / 5
 * row still produced (e.g. by the retiring extractor) shows in an "Other Classifications" section
 * instead of being dropped.
 */
export {
    BALANCE_SHEET_LAYOUT,
    PROFIT_AND_LOSS_LAYOUT,
    CASH_FLOW_LAYOUT,
} from '../../../AuditedFinancialsVisualisation/auditedStatementLayout';

/**
 * Appends any payload classification the fixed layout has no row for, so nothing the backend
 * returned can disappear (RECUR-27640).
 *
 * `AuditedStatementTable` renders `layout.map(...)` and looks each row up by `src` — rows are
 * driven entirely by the layout, so a classification the layout does not name is invisible with no
 * count, no residual bucket and no signal. Pre-RECUR-27640 provisional Cash Flow rendered through
 * ClassifiedTable, which showed whatever came back; this keeps that losslessness while gaining the
 * statutory ordering.
 *
 * Aggregated rows are skipped — a backend subtotal that the layout deliberately omits (e.g. the
 * intentionally-dropped `Total Liabilities`) is not missing data and must not reappear as an item.
 */
export const appendUnmatchedRows = (layout: AuditedRowSpec[], tableData: any): AuditedRowSpec[] => {
    const known = new Set(layout.map((row) => row.src).filter(Boolean));
    const unmatched = (tableData?.rows ?? []).filter(
        (row: any) => row?.classificationName && !row.isAggregated && !known.has(row.classificationName),
    );
    if (!unmatched.length) return layout;
    return [
        ...layout,
        { type: 'group', label: 'Other Classifications', src: null },
        ...unmatched.map((row: any) => ({
            type: 'item' as const,
            label: row.classificationName,
            src: row.classificationName,
        })),
    ];
};
